// Field splitter for combat log lines.
//
// A log line looks CSV-ish but the field count varies per event, strings are
// quoted with escaped inner quotes, and COMBATANT_INFO embeds `[...]` / `(...)`
// structures that contain their own commas. Splitting therefore has to be
// quote- and bracket-aware rather than a plain `split(',')`.

const COMMA = 44
const QUOTE = 34
const BACKSLASH = 92
const OPEN_BRACKET = 91
const CLOSE_BRACKET = 93
const OPEN_PAREN = 40
const CLOSE_PAREN = 41

// Returns the index right after the closing quote of the string that starts at
// `start`. An unterminated string consumes the rest of the line, which keeps a
// truncated last line from throwing.
function skipQuoted(text, start) {
    let index = start + 1
    const length = text.length
    while (index < length) {
        const code = text.charCodeAt(index)
        if (code === BACKSLASH) {
            index += 2
            continue
        }
        if (code === QUOTE) return index + 1
        index += 1
    }
    return length
}

function skipGrouped(text, start) {
    let index = start
    const length = text.length
    let depth = 0
    while (index < length) {
        const code = text.charCodeAt(index)
        if (code === QUOTE) {
            index = skipQuoted(text, index)
            continue
        }
        if (code === OPEN_BRACKET || code === OPEN_PAREN) depth += 1
        else if (code === CLOSE_BRACKET || code === CLOSE_PAREN) {
            depth -= 1
            if (depth <= 0) return index + 1
        }
        index += 1
    }
    return length
}

export function splitFields(body) {
    const fields = []
    const length = body.length
    let fieldStart = 0
    let index = 0

    while (index < length) {
        const code = body.charCodeAt(index)

        if (code === COMMA) {
            fields.push(body.slice(fieldStart, index))
            index += 1
            fieldStart = index
            continue
        }

        if (code === QUOTE) {
            index = skipQuoted(body, index)
            continue
        }

        if (code === OPEN_BRACKET || code === OPEN_PAREN) {
            index = skipGrouped(body, index)
            continue
        }

        index += 1
    }

    fields.push(body.slice(fieldStart, length))
    return fields
}

// `"Команда \"Взять!\""` -> `Команда "Взять!"`
export function unquote(value) {
    if (!value) return ''
    if (value.length > 1 && value.charCodeAt(0) === QUOTE && value.charCodeAt(value.length - 1) === QUOTE) {
        const inner = value.slice(1, -1)
        return inner.indexOf('\\') >= 0 ? inner.replace(/\\(.)/g, '$1') : inner
    }
    return value
}

// Parses the `[(1,2,3),(4,5,6)]` style structures used by COMBATANT_INFO into
// nested arrays of strings.
export function parseGrouped(value) {
    const text = String(value || '').trim()
    if (!text) return []
    const first = text.charCodeAt(0)
    if (first !== OPEN_BRACKET && first !== OPEN_PAREN) return unquote(text)

    const inner = text.slice(1, -1)
    if (!inner.trim()) return []
    return splitFields(inner).map((item) => parseGrouped(item.trim()))
}

export function toNumber(value) {
    if (value === undefined || value === null || value === '' || value === 'nil') return 0
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
}

export function toBool(value) {
    return value === '1' || value === 'true' || value === 'TRUE'
}

export function isNilLike(value) {
    return value === undefined || value === '' || value === 'nil'
}
